'use client'

import { cn } from '@/_lib/utils'

import {
	canReachOrderFlowStep,
	type OrderFlowStepReachabilityContext
} from '../../../_utils/order-flow-step-guards'

import {
	AOrderProgressStepNavBackButton,
	AOrderProgressStepNavForwardButton,
	ORDER_PROGRESS_ARROW_OUTER_CLASS
} from './a-order-progress-arrow-buttons'

import type {
	OrderFlowStep,
	OrderFlowStepDefinition
} from '../../../_constants/order-flow-steps.const'

type AOrderProgressDesktopProps = {
	className?: string
	stepDefs: OrderFlowStepDefinition[]
	currentStep: OrderFlowStep
	maxStepReached: OrderFlowStep
	prevStep: OrderFlowStep
	nextStep: OrderFlowStep
	canGoBack: boolean
	canGoForward: boolean
	showBackArrow: boolean
	showForwardArrow: boolean
	goToStep: (step: OrderFlowStep) => void
	reachability: OrderFlowStepReachabilityContext
}

/** Large-screen labeled step track with the same prev/next arrows as mobile (hidden on small viewports). */
export function AOrderProgressDesktop({
	className,
	stepDefs,
	currentStep,
	maxStepReached,
	prevStep,
	nextStep,
	canGoBack,
	canGoForward,
	showBackArrow,
	showForwardArrow,
	goToStep,
	reachability
}: AOrderProgressDesktopProps) {
	return (
		<div className={cn('hidden w-full rounded-2xl lg:block', className)}>
			<div className='flex w-full items-center gap-3'>
				<div className={ORDER_PROGRESS_ARROW_OUTER_CLASS}>
					{showBackArrow ? (
						<AOrderProgressStepNavBackButton
							disabled={!canGoBack}
							onClick={() => goToStep(prevStep)}
						/>
					) : null}
				</div>

				<div className='min-w-0 flex-1'>
					<div className='flex gap-8'>
						{stepDefs.map(({ step, label }, idx) => {
							const isClickable =
								step <= maxStepReached && canReachOrderFlowStep(step, reachability)
							const isVisitedFuture = step > currentStep && step <= maxStepReached
							const isPast = step < currentStep
							const isCurrent = step === currentStep
							const isLocked = step > maxStepReached

							return (
								<button
									key={step}
									type='button'
									className={cn(
										'group flex flex-1 flex-col gap-1 text-left',
										isClickable && 'cursor-pointer',
										!isClickable && 'cursor-default'
									)}
									onClick={() => goToStep(step)}
									disabled={!isClickable}
									aria-current={isCurrent ? 'step' : undefined}
								>
									<div className='px-1'>
										<p className='text-sm font-medium'>
											0{idx + 1}. {label}
										</p>
									</div>
									<div
										className={cn(
											'h-[3px] w-full rounded-full transition-colors duration-300 ease-out',
											isPast && 'bg-medante-pink-secondary',
											isCurrent && 'bg-medante-pink-secondary',
											isVisitedFuture && 'bg-medante-purple/50',
											isLocked && 'bg-gray-200',
											isVisitedFuture && 'group-hover:bg-medante-purple-dark'
										)}
									/>
								</button>
							)
						})}
					</div>
				</div>

				<div className={ORDER_PROGRESS_ARROW_OUTER_CLASS}>
					{showForwardArrow ? (
						<AOrderProgressStepNavForwardButton
							disabled={!canGoForward}
							onClick={() => goToStep(nextStep)}
						/>
					) : null}
				</div>
			</div>
		</div>
	)
}
